import { useState, useEffect, ReactElement } from "react"
import {
    GPUState,
    Tick,
    UseGPUFn,
    UseGpuReturnType,
    Unit,
    Character,
    Pattern,
    isPattern,
    GPUOptions,
    RenderOutput,
    Render,
    Resolution,
    RenderInput,
} from "./types"
import UnitStyled from "./Unit.styled"
import RowStyled from "./Row.styled"

const MAX_TICKS = Math.pow(2, 16)

const renderCharacter = (character: Character) => {
    return typeof character === "string" ? character.replace(/ /g, "\u00A0") : character
}

const renderPattern = (pattern: Pattern, tick: Tick) => {
    const iteration = pattern[tick % pattern.length]
    return renderCharacter(iteration)
}

const renderUnit = (unit: Unit, tick: Tick, position: { x: number, y: number }): ReactElement => (
    <UnitStyled key={`${position.x}_${position.y}`} onClick={() => { document.dispatchEvent(new CustomEvent<{ position: { x: number, y: number } }>("hugo-onclick", { detail: { position } })) }}>
        {isPattern(unit) ? renderPattern(unit, tick) : renderCharacter(unit)}
    </UnitStyled>
)

const renderRow = (row: Unit[], tick: Tick, resolution: Resolution, y: number = 0) => {
    return (
        <RowStyled key={y}>
            {row.map((unit, x) => renderUnit(unit, tick, { x, y }))}
        </RowStyled>
    )
}

const render: Render = (input, state, resolution, shaders = [], plugins = []): RenderOutput => {
    let renderInput = input

    if (Array.isArray(shaders)) {
        plugins.forEach((plugin) => {
            renderInput = plugin(renderInput, state, resolution)
        })
    }

    if (Array.isArray(shaders)) {
        shaders.forEach((shader) => {
            renderInput = shader(renderInput, state, resolution)
        })
    }

    return <>{renderInput.map((row, y) => renderRow(row, state, resolution, y))}</>
}

export const renderStaticString = (input: RenderInput): string => {
    let renderInput = [...input]
    return renderInput.map((row) => row.map((unit) => isPattern(unit) ? renderPattern(unit, 0) : renderCharacter(unit)).join('')).join('\n')
}

const useGPU: UseGPUFn = ({ tickRate, resetOnMax = true, resolution }: GPUOptions): UseGpuReturnType => {
    const [state, setState] = useState<GPUState>(0)


    useEffect(() => {
        const tickInterval = setInterval(() => {
            setState(currentState => {
                let next = currentState + 1
                if (next > MAX_TICKS && resetOnMax) next = 0
                return next
            })
        }, tickRate)

        return () => {
            clearInterval(tickInterval)
        }
    }, [tickRate])


    const setTick = (tick: number) => {
        if (tick < 0) throw new Error("You cannott set tick to less than 0")
        if (tick > MAX_TICKS) throw new Error(`You cannott set tick to greater than ${MAX_TICKS}`)

        setState(tick)
    }

    const getMaxTick = () => MAX_TICKS

    return [
        (input, shaders, plugins) => render(input, state, resolution, shaders, plugins),
        state,
        {
            setTick,
            getMaxTick,
        }
    ]
}

export default useGPU